import { Tab } from '@headlessui/react'
import { useCallback, useContext, useEffect, useState } from 'react'
import FlowContext from '../contextFlow'

function PressButtonModal({ id, data, setIsOpen }) {
  const { triggerUpdate, elements } = useContext(FlowContext)
  const [name, setName] = useState(data.name || '')
  const [selectedTab, setSelectedTab] = useState(
    data.digitPrompt ? 2 : data.sequence ? 1 : 0,
  )
  const [digit, setDigit] = useState(data.digit || null)
  const [sequence, setSequence] = useState(data.sequence || '')
  const [digitPrompt, setDigitPrompt] = useState(data.digitPrompt || '')
  const [goToNextNode, setGoToNextNode] = useState(data.goToNextNode || false)
  const [targetNodeAfterPress, setTargetNodeAfterPress] = useState(data.targetNodeAfterPress || null)

  const numpadOptions = [
    ...Array.from({ length: 10 }).keys(),
    { key: '* (star)', value: '*' },
    { key: '# (pound)', value: '#' },
  ]

  const availableNodes = elements.nodes.filter(node => node.id !== id)

  const updateField = useCallback(
    (field, value) => {
      if (field === 'goToNextNode') {
        setGoToNextNode(value)
        if (!value) {
          setTargetNodeAfterPress(null)
        }
      }
      else if (field === 'targetNodeAfterPress') {
        setTargetNodeAfterPress(value)
      }
    },
    [],
  )

  // Handle edge creation/removal for go-to-next-node functionality
  useEffect(() => {
    if (id && goToNextNode && targetNodeAfterPress?.id) {
      const currentEdges = elements.edges
      const existingGoToEdges = currentEdges.filter(edge =>
        edge.data?.isGoToNextNodeEdge && edge.source === id,
      )

      const edgeId = `${id}-${targetNodeAfterPress.id}`
      const edgeExists = currentEdges.some(edge => edge.id === edgeId)

      if (!edgeExists) {
        const newEdge = {
          id: edgeId,
          source: id,
          target: targetNodeAfterPress.id,
          type: 'custom',
          animated: true,
          data: {
            label: 'After Press',
            isGoToNextNodeEdge: true,
          },
        }

        const updatedEdges = existingGoToEdges.length > 0
          ? currentEdges.filter(edge => !(edge.data?.isGoToNextNodeEdge && edge.source === id)).concat(newEdge)
          : [...currentEdges, newEdge]

        triggerUpdate({
          nodes: elements.nodes,
          edges: updatedEdges,
        })
      }
    }
    else if (id && (!goToNextNode || !targetNodeAfterPress?.id)) {
      // Remove existing go-to-next edges when option is disabled or no target selected
      const currentEdges = elements.edges
      const existingGoToEdges = currentEdges.filter(edge =>
        edge.data?.isGoToNextNodeEdge && edge.source === id,
      )

      if (existingGoToEdges.length > 0) {
        const updatedEdges = currentEdges.filter(edge =>
          !(edge.data?.isGoToNextNodeEdge && edge.source === id),
        )

        triggerUpdate({
          nodes: elements.nodes,
          edges: updatedEdges,
        })
      }
    }
  }, [goToNextNode, targetNodeAfterPress, id, elements, triggerUpdate])

  const handleSave = () => {
    const updatedNodes = elements.nodes.map((el) => {
      if (el.id === id) {
        const updatedData = {
          name,
          ...(selectedTab === 0 && digit ? { digit } : {}),
          ...(selectedTab === 1 && sequence ? { sequence } : {}),
          ...(selectedTab === 2 && digitPrompt ? { digitPrompt } : {}),
          modelOptions: {
            ...data.modelOptions,
            skipUserResponse: goToNextNode,
          },
          goToNextNode,
          ...(targetNodeAfterPress ? { targetNodeAfterPress } : {}),
          isStart: data.isStart ?? false,
        }

        return {
          ...el,
          data: updatedData,
          type: 'Press Button',
        }
      }
      return el
    })

    setIsOpen(false)
    triggerUpdate({ nodes: updatedNodes }, false)
  }

  return (
    <div className="flex flex-col gap-y-4">
      <div>
        <label className="mb-1.5 block font-medium text-gray-700">Name:</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          className="w-full rounded border border-gray-300 p-1.5"
        />
      </div>

      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex gap-x-0.5 rounded-lg bg-indigo-100 p-0.5">
          <Tab
            className={({ selected }) =>
              selected
                ? 'bg-indigo-500 text-white rounded-md py-1.5 px-3'
                : 'text-gray-900 rounded-md py-1.5 px-3'}
          >
            Single Digit
          </Tab>
          <Tab
            className={({ selected }) =>
              selected
                ? 'bg-indigo-500 text-white rounded-md py-1.5 px-3'
                : 'text-gray-900 rounded-md py-1.5 px-3'}
          >
            Sequence of Digits
          </Tab>
          <Tab
            className={({ selected }) =>
              selected
                ? 'bg-indigo-500 text-white rounded-md py-1.5 px-3'
                : 'text-gray-900 rounded-md py-1.5 px-3'}
          >
            Agent Decision
          </Tab>
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel className="flex flex-col gap-y-1.5">
            <label className="block text-[12px] font-medium text-gray-950">
              Button Digit to Press:
            </label>
            <select
              value={digit}
              onChange={e => setDigit(e.target.value)}
              className={`
                block w-full rounded-md border border-gray-300 p-1.5
                text-gray-900
                focus:border-teal-500 focus:ring-teal-500
              `}
            >
              <option value="">Select a Digit</option>
              {numpadOptions.map(option => (
                <option
                  key={option.key || option}
                  value={option.value || option}
                >
                  {option.key || option}
                </option>
              ))}
            </select>
            <label className="mt-1.5 block text-xs text-gray-700">
              Specify a single digit to be pressed automatically.
            </label>
          </Tab.Panel>
          <Tab.Panel className="flex flex-col gap-y-1.5">
            <label className="block text-[12px] font-medium text-gray-950">
              Sequence of Digits:
            </label>
            <input
              type="text"
              value={sequence}
              onChange={e => setSequence(e.target.value)}
              className="w-full rounded border border-gray-300 p-1.5"
            />
            <label className="mt-1.5 block text-xs text-gray-700">
              Specify a sequence of digits to be pressed in order.
            </label>
          </Tab.Panel>
          <Tab.Panel className="flex flex-col gap-y-1.5">
            <label className="block text-[12px] font-medium text-gray-950">
              Agent Decision:
            </label>
            <textarea
              value={digitPrompt}
              onChange={e => setDigitPrompt(e.target.value)}
              className="w-full rounded border border-gray-300 p-1.5"
              rows="4"
            >
            </textarea>
            <label className="mt-1.5 block text-xs text-gray-700">
              Provide a prompt for the agent to decide the action based on the
              current conversation context.
            </label>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      <div className="mt-4">
        <label className="mb-1 block text-xs font-medium text-gray-700">
          After Pressing Button
        </label>
        <select
          value={goToNextNode ? 'go-to-node' : 'do-nothing'}
          onChange={(e) => {
            const value = e.target.value
            updateField('goToNextNode', value === 'go-to-node')
            if (value === 'do-nothing') {
              updateField('targetNodeAfterPress', null)
            }
          }}
          className={`
            block w-full rounded-md border border-gray-300 p-1.5 text-sm
            text-gray-900
            focus:border-indigo-500 focus:ring-indigo-500
          `}
        >
          <option value="do-nothing">Do Nothing (Wait for user input)</option>
          <option value="go-to-node">Go to Specific Node</option>
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Choose what happens after the button is pressed.
        </p>

        {goToNextNode && (
          <div className="mt-2">
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Select Target Node
            </label>
            <select
              value={JSON.stringify(targetNodeAfterPress || { id: '', name: '' })}
              onChange={(e) => {
                const value = JSON.parse(e.target.value)
                const selectedNode = availableNodes.find(node => node.id === value.id)
                updateField('targetNodeAfterPress', {
                  id: value.id,
                  name: selectedNode?.data.name || '',
                })
              }}
              className={`
                block w-full rounded-md border border-gray-300 p-1.5 text-sm
                text-gray-900
                focus:border-indigo-500 focus:ring-indigo-500
              `}
            >
              <option value={JSON.stringify({ id: '', name: '' })}>Select a node to go to...</option>
              {availableNodes
                .filter(node => node.data && node.data.name)
                .slice()
                .sort((a, b) => a.data.name.localeCompare(b.data.name))
                .map(node => (
                  <option
                    key={node.id}
                    value={JSON.stringify({
                      id: node.id,
                      name: node.data.name,
                    })}
                  >
                    {node.data.name}
                  </option>
                ))}
            </select>
          </div>
        )}
      </div>

      <button
        onClick={handleSave}
        className="mt-2.5 rounded bg-indigo-500 px-2.5 py-1.5 text-white"
      >
        Save
      </button>
    </div>
  )
}

export default PressButtonModal
